// TERM2 (docs/build-briefs/TERM2-physical-reader-registration.md): the plain
// words for a registered card reader and for a payment taken on one. Pure and
// DOM-free so each sentence is proven by a unit test. A reader the provider
// reports is described from the provider's answer; nothing here invents a
// status when the provider said nothing.

/** What GET /tenants/:tenantId/terminals reports for one reader. */
export interface TerminalReaderDto {
  deviceType: string | null;
  serialNumber: string | null;
  connection: "online" | "offline" | "unknown";
  simulated: boolean;
}

/** The reader's family in words: "Stripe Reader S710", "Simulated reader", or
 * the provider's own type made readable when it is one this screen does not
 * name. */
export function readerDeviceLabel(reader: TerminalReaderDto): string {
  const type = reader.deviceType;
  if (
    reader.simulated ||
    (type !== null && type.startsWith("simulated_")) ||
    type === "simulated_reader"
  ) {
    return "Simulated reader";
  }
  if (type === null) {
    return "Card reader";
  }
  const stripe = /^stripe_(s\d+)$/.exec(type);
  if (stripe !== null && stripe[1] !== undefined) {
    return `Stripe Reader ${stripe[1].toUpperCase()}`;
  }
  return type
    .split("_")
    .filter((part) => part.length > 0)
    .map((part, index) => (index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(" ");
}

export function readerConnectionLabel(reader: TerminalReaderDto | null | undefined): string {
  if (reader === null || reader === undefined) {
    return "Status unavailable";
  }
  switch (reader.connection) {
    case "online":
      return "Online";
    case "offline":
      return "Offline";
    default:
      return "Status unavailable";
  }
}

/** The device line under a terminal's name in Settings: the reader's family and
 * serial. The connection state is its own chip (readerStatusChip), always with
 * a word, so it never depends on colour alone. */
export function readerDetailLine(
  reader: TerminalReaderDto | null | undefined,
  fallbackLabel: string,
): string {
  if (reader === null || reader === undefined) {
    return fallbackLabel;
  }
  const parts = [readerDeviceLabel(reader)];
  if (reader.serialNumber !== null && reader.serialNumber.length > 0) {
    parts.push(`Serial ${reader.serialNumber}`);
  }
  return parts.join(" · ");
}

export type ReaderChipTone = "success" | "danger" | "neutral";

/** The status chip beside a reader: success tint Online, danger tint Offline,
 * neutral "Status unavailable". `canRecheck` is true only for the unavailable
 * one, which offers a "Check again" button. */
export function readerStatusChip(reader: TerminalReaderDto | null | undefined): {
  label: string;
  tone: ReaderChipTone;
  canRecheck: boolean;
} {
  const label = readerConnectionLabel(reader);
  if (label === "Online") {
    return { label, tone: "success", canRecheck: false };
  }
  if (label === "Offline") {
    return { label, tone: "danger", canRecheck: false };
  }
  return { label, tone: "neutral", canRecheck: true };
}

/** How long a card payment must be in flight before the waiting line shows, so
 * a simulated reader (which answers at once) never flashes it. */
export const WAITING_LINE_DELAY_MS = 600;

export const WAITING_TITLE = "Waiting for the card on the reader.";
export const WAITING_HINT = "Ask the guest to tap, insert or swipe.";
/** TERM10 (DEC-410). The waiting line when the guest picks the tip and pays on the reader. */
export const ON_READER_TITLE = "On reader";
export const ON_READER_HINT = "Hand the reader to the guest.";
export const WAITING_FOR_CARD_TEXT = `${WAITING_TITLE} ${WAITING_HINT}`;
export const CANCEL_WAIT_LABEL = "Cancel and take payment another way";

export const TERMINAL_NAME_LABEL = "Card reader name";
export const REGISTRATION_CODE_LABEL = "Reader code (optional)";

export type PayStatusTone = "success" | "warning" | "error";

/** TERM6-F1. Why a payment did not complete, as the server states it (the pay and collect responses' `failureKind`). */
export type PayFailureKind = "declined" | "customer_canceled" | "reader_error";

/** Three states, each with its next action and no explanation of why (owner copy rule). Pending designer approval. The
 * cancel sentence is the one that says nothing was charged, because the server cancelled the payment after a confirmed
 * guest cancel; no other sentence claims it. */
export const DECLINED_TEXT = "Card declined. Try another card.";
export const CUSTOMER_CANCELED_TEXT = "Canceled on the reader. Nothing was charged. Try again.";
export const READER_ERROR_TEXT = "Reader failed. Check the reader, then try again.";
/** The sentence when the server gave no kind (a replay, an unclassifiable provider status). */
export const FAILED_GENERIC_TEXT =
  "The payment did not complete. Check the reader screen before trying again.";

/** The Failed sentence for a server-stated kind; the generic one for anything else, including an unknown value. */
export function failedPaymentText(failureKind: string | null | undefined): string {
  switch (failureKind) {
    case "declined":
      return DECLINED_TEXT;
    case "customer_canceled":
      return CUSTOMER_CANCELED_TEXT;
    case "reader_error":
      return READER_ERROR_TEXT;
    default:
      return FAILED_GENERIC_TEXT;
  }
}

/** The sentence and tone a payment result shows on the Pay sheet. Captured
 * keeps the wording the app has always used. Failed stays red but no longer
 * promises "nothing was charged": the server maps an unrecognised provider
 * status to Failed too, so only a confirmed cancel may say so. Pending is a
 * caution, not an error. */
export function payResultView(params: {
  amount: string;
  status: string;
  guestLabel?: string;
  /** The tip inside `amount`, in cents. UX102: the figure charged to the card includes it, the ticket's Paid row
   * does not (UX62), so the sentence says how much of the charge was tip and the two figures reconcile. */
  tipCents?: number | null;
  /** TERM6-F1: the server's reason for a Failed payment, when it gave one. */
  failureKind?: string | null;
  /** CASH1-S2-U1. A cash payment: no card was approved, so the sentence says cash was taken. */
  cash?: boolean;
}): {
  text: string;
  tone: PayStatusTone;
} {
  const forGuest = params.guestLabel === undefined ? "" : ` for ${params.guestLabel}'s drinks`;
  if (params.status === "Captured") {
    const tip =
      params.tipCents === undefined || params.tipCents === null || params.tipCents <= 0
        ? ""
        : ` with $${(params.tipCents / 100).toFixed(2)} tip`;
    return {
      text:
        params.cash === true
          ? `Paid $${params.amount}${forGuest}. Cash taken.`
          : `Paid $${params.amount}${forGuest}${tip}. Card approved.`,
      tone: "success",
    };
  }
  if (params.status === "Failed") {
    return {
      text: failedPaymentText(params.failureKind),
      // A guest who cancelled chose to; it is not a fault, so it is a caution (designer review, TERM6-F1 section 7).
      tone: params.failureKind === "customer_canceled" ? "warning" : "error",
    };
  }
  return {
    text: "Not confirmed yet. Check the reader screen before trying again.",
    tone: "warning",
  };
}

/** TERM6-F3. What a split share's collect result means on the floor. Only a Captured attempt is a collected share; any
 * other status (Failed, Pending, anything new) is not, and carries the same sentence and tone a whole-ticket payment
 * would show for it. `keepKey` is true only for Pending, where the answer is not final and a retry must reuse the key. */
export function shareCollectView(params: {
  amount: string;
  status: string;
  failureKind?: string | null;
}): {
  collected: boolean;
  keepKey: boolean;
  text: string;
  tone: PayStatusTone;
} {
  const view = payResultView({
    amount: params.amount,
    status: params.status,
    failureKind: params.failureKind,
  });
  return {
    collected: params.status === "Captured",
    keepKey: params.status !== "Captured" && params.status !== "Failed",
    text: view.text,
    tone: view.tone,
  };
}

export function payResultText(params: {
  amount: string;
  status: string;
  guestLabel?: string;
  tipCents?: number | null;
}): string {
  return payResultView(params).text;
}

export type CancelReaderOutcome = "canceled" | "completed" | "unresolved" | "nothing_pending";

export const PAYMENT_CANCELLED_TEXT = "Payment cancelled. Nothing was charged.";

/** What the Pay line says after the cashier's Cancel, from what the server read
 * back from the provider. "Nothing was charged" is said ONLY for `canceled`. */
export function cancelOutcomeView(outcome: CancelReaderOutcome): {
  text: string;
  tone: PayStatusTone;
} {
  switch (outcome) {
    case "canceled":
      return { text: PAYMENT_CANCELLED_TEXT, tone: "success" };
    case "completed":
      return {
        text: "The card was accepted just before it was cancelled. The payment went through.",
        tone: "success",
      };
    case "nothing_pending":
      return {
        text: "Nothing is waiting on the reader. Check the ticket before trying again.",
        tone: "warning",
      };
    default:
      return {
        text: "Could not confirm the payment was cancelled. Check the reader screen before trying again.",
        tone: "warning",
      };
  }
}

/** LOUNGERMAJ1 (lounger-07): the receipt sheet's title once a card payment closed the ticket. `amount` is the charge in
 * dollars as the server wrote it (the tip is inside it); the check mark is the shape beside the words. */
export function receiptPaidTitle(amount: string, tipCents: number | null | undefined): string {
  const tip =
    tipCents === undefined || tipCents === null || tipCents <= 0
      ? ""
      : ` (tip $${(tipCents / 100).toFixed(2)})`;
  return `\u2713 Paid $${amount}${tip}`;
}
